import { useCallback, useEffect, useState } from 'react';
import Video, { LocalVideoTrackPublication, Room } from 'twilio-video';
import { useRuntimeConfig } from '@vl-core/hooks/useConfig';
import useUser from 'vl-core/hooks/useUser';
import { errorModal } from 'vl-common/src/components/Modals';
import { Token } from '@hooks/video/useChimeVideoCall';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { useAppointmentRoomDT } from '@hooks/useTestPatientRoom';
import { Appointment } from 'vl-common/src/schemas/shared';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';
import { useTranslation } from 'react-i18next';
import useMediaPermissions from './useMediaPermissions';
import { getPreferredVideoInput } from '@lib/cameraPreference';
import { videoBackground } from '@lib/videoBackgrounds/backgrounds';
import useMyRole from '@hooks/useMyRole';

let globalActiveRoom: Room | undefined;

const getIsBrowserError = (errorMessage: string): boolean => {
  const BROWSER_ERROR = 'DOMException:';
  return errorMessage?.startsWith(BROWSER_ERROR);
};

interface PermissionsError {
  code: number;
  message: string;
  name: string;
}

const BROWSER_ERROR_MESSAGE =
  "Could not connect to the call room due to a browser error. Please check your browser's camera and/or microphone " +
  'permissions and refresh this page. If the error persists, try using a different browser or device.';

/**
 * Returns a connected Twilio room associated with a roomName and its token. Will reconnect (and return
 * a new room instance) if the room disconnects
 */
export function useRoomWS(
  roomName: string,
  token: string | Token,
  publishTracks = true
): { room: Room; error: string } {
  const [retry, setRetry] = useState(0);
  const [room, setRoom] = useState<Room>();
  const [error, setError] = useState('');
  const role = useMyRole();
  const { PVIDEO, VIDEO_BACKGROUNDS_ENABLED } = useRuntimeConfig();
  const canUseVideoBackground = VIDEO_BACKGROUNDS_ENABLED && role === 'clinician';
  const { requestPermissions, checkCurrentPermissions } = useMediaPermissions();
  const { user_guid } = useUser();
  const { t } = useTranslation();
  const sendTwilioEvents = useSendTwilioEvents();
  const appointment_id = +roomName.split('-').at(-1);
  const logMessage = useCallback(
    (msg: string) =>
      sendTwilioEvents({
        userGuid: user_guid,
        appointmentId: appointment_id,
        event: 'USRLOG',
        message: msg
      }),
    [appointment_id, sendTwilioEvents, user_guid]
  );

  useEffect(() => {
    let isCancelled = false;
    let localRoomRef: Room | undefined;

    async function checkAndLogError(error: (Error & { code?: number }) | PermissionsError) {
      const isBrowserError = getIsBrowserError(error?.message);
      logMessage(JSON.stringify({ name: error.name, message: error.message }));
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasAudio = devices.some((d) => d.kind === 'audioinput');
      const hasVideo = devices.some((d) => d.kind === 'videoinput');

      if (!hasAudio || !hasVideo) {
        const missingItems = [];
        if (!hasVideo) missingItems.push('camera');
        if (!hasAudio) missingItems.push('microphone');
        const missingHardware = missingItems.join(' and ');

        setError(
          t(
            `We are unable to detect a working {{missingHardware}}. Please ensure your equipment is connected and enabled, then refresh the page.`,
            { missingHardware }
          )
        );
        return;
      }

      if ('code' in error && error.code === 53118 && role === 'patient') {
        console.log('Clinician has ended the video call - room closed');
        return;
      }

      console.log({ error });
      console.log(`%c checkError: ${error?.message}`, 'color: green');
      console.log(`%c checkError: ${JSON.stringify(error)}`, 'color: green');

      if (isBrowserError) {
        setError(BROWSER_ERROR_MESSAGE);
        return;
      }

      if (error?.message === 'Permission denied') {
        errorModal({
          title: t('Permission Denied'),
          content: t(
            "Could not connect to video camera and/or microphone. Please check your browser's permissions, adjust and refresh this page."
          )
        });

        return;
      }

      if ('code' in error && error.code === 53205) {
        // todo - invoke an API to force a participant disconnect
        console.log('Reloading because of Twilio duplicated identity');
        window.location.reload();
      } else {
        setError(
          `Failed to connect to video call. Please try refreshing the page.${
            error?.message ? ` Error: ${error?.message}` : ''
          }`
        );
      }
    }

    async function connect() {
      if (globalActiveRoom) {
        console.log('Force-closing previous room connection');
        globalActiveRoom.disconnect();
        globalActiveRoom = undefined;
      }

      const getSessionAppointmentId = () =>
        Buffer.from(new URLSearchParams(window.location.search).get('req') || '', 'base64').toString();

      try {
        let permissions = await checkCurrentPermissions();

        if (!permissions.audio || !permissions.video) {
          permissions = await requestPermissions();
        }

        console.log(`%c token = ${JSON.stringify(token)}, roomName = ${roomName}`, 'color: green');
        const tracks = publishTracks ? {} : { tracks: [] };
        const startingAppointment = getSessionAppointmentId();
        /**
         * We need to know the audio/video permissions before connecting.
         * If we set video = true and audio = true, but one or both fail in Video.connect(), then
         * the function will throw and the user will be unable to join the call. To avoid being
         * blocked from joining the call we join with only the permissions we know are enabled.
         *
         * It is a requirement that video calls can be joined with out video and/or audio:
         * https://hbsuk.atlassian.net/browse/VLNHS-3696
         */
        const video = permissions.video ? await getPreferredVideoInput() : false;

        const room = await Video.connect(token, {
          name: roomName,
          region: 'ie1',
          preferredVideoCodecs: [{ codec: 'VP8' }],
          logLevel: 'off',
          audio: permissions.audio,
          video,
          networkQuality: {
            local: 3,
            remote: 3
          },
          ...tracks
        });

        if (isCancelled) {
          room.disconnect();
          return;
        }

        globalActiveRoom = room;
        localRoomRef = room;

        room.on('disconnected', () => {
          if (globalActiveRoom === room) globalActiveRoom = undefined;
        });

        // video.connect() can take a long time ... need to ensure the user hasn't switched away in the interim
        if (startingAppointment !== getSessionAppointmentId() || !getSessionAppointmentId()) {
          console.log('Bailing because search parameters changed');
          room.disconnect();
          return;
        }

        logMessage(
          JSON.stringify({
            room: { name: roomName, sid: room.sid, localParticipant: { sid: room.localParticipant.sid } }
          })
        );

        console.log(`%c room.state = ${room.state}`, 'color: red');
        if (room.state !== 'connected') {
          setRetry((r) => r + 1);
          return;
        }

        setError('');

        setRoom(room);
        window.addEventListener('pagehide', () => room.disconnect());

        room.localParticipant.on('trackStopped', () => console.log('TrackStopped'));
        room.localParticipant.on('trackStarted', () => console.log('TrackStarted'));

        if (canUseVideoBackground) {
          const attachBackground = (publication: LocalVideoTrackPublication) => {
            if (publication.kind !== 'video' || publication.trackName === 'screen') return;
            videoBackground.attachTo(publication.track).then();
          };

          room.localParticipant.videoTracks.forEach(attachBackground);
          room.localParticipant.on('trackPublished', attachBackground);
          room.on('disconnected', () => videoBackground.detach());

          videoBackground.restoreSaved().then();
        }

        room.on('disconnected', (room, error) => error && checkAndLogError(error));
      } catch (error) {
        checkAndLogError(error);
      }
    }

    if (roomName && token) {
      if (PVIDEO === 'chime') {
        console.log('using chime', roomName, token);
      } else {
        connect().then();

        return () => setRoom(undefined);
      }
    }

    return () => {
      isCancelled = true;
      if (localRoomRef) {
        localRoomRef.disconnect();
        if (globalActiveRoom === localRoomRef) globalActiveRoom = undefined;
      }
      setRoom(undefined);
    };
  }, [roomName, token, retry]);

  return {
    room,
    error
  };
}

function useRoomWSAdapter(roomName: string, token: string | Token, _appointment: Appointment, publishTracks: boolean) {
  return useRoomWS(roomName, token, publishTracks);
}

function useRoomDT(_roomName: string, _token: string | Token, appointment: Appointment) {
  const { room, error } = useAppointmentRoomDT(appointment);

  return { room, error };
}

// Returns the call status of the given appointment, which *must* be stable. Switches between implementations.
export function useRoom(roomName: string, token: string | Token, appointment: Appointment, publishTracks = true) {
  const useRoomImpl = useVideoCallSignallingType(appointment) === 'ws' ? useRoomWSAdapter : useRoomDT;

  return useRoomImpl(roomName, token, appointment, publishTracks);
}

export default useRoom;
